/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createEvent, updateEvent } from 'api/event' // Ensure import of API functions
import { FileUploadUploadEvent } from 'primereact/fileupload'
import { useCallback, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { CreateEventDto, CreateTicket, EventType, SeatSellingMode } from 'types/Event'
import { ImageType } from 'types/Image'
import { LocationType, SeatStatus } from 'types/Location'
import { ShowImageData } from 'types/Show'

export interface UseEventFormProps {
    initialData?: EventType | any
    locationData?: LocationType
    generatedEventId?: string
    isEditMode?: boolean
    onClose?: () => void
    showConfirmDialog?: (onConfirm: () => void, headerText?: string, confirmMessage?: string) => void
    partnerId?: string
    seatSellingMode?: SeatSellingMode
}

export const useEventsForm = ({
    initialData,
    locationData,
    generatedEventId,
    isEditMode = false,
    onClose,
    showConfirmDialog,
    partnerId,
}: UseEventFormProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<any>({
        defaultValues: initialData ?? {},
    })

    const [loading, setLoading] = useState(false)
    const [uploadedImages, setUploadedImages] = useState<ShowImageData[]>(initialData?.images || [])
    const [customImageOptions, setCustomImageOptions] = useState<ImageType[]>(initialData?.images || [])
    const [customImage, setCustomImage] = useState<string | null>(initialData?.custom_image || null)
    const [ticketData, setTicketData] = useState<CreateTicket[]>(initialData?.tickets || [])

    const { fields, append, remove } = useFieldArray({ control, name: 'pricing' })

    const onUpload = useCallback((event: FileUploadUploadEvent) => {
        try {
            const jsonResponse = JSON.parse(event.xhr.responseText)
            setUploadedImages((prevImages) => [...prevImages, ...jsonResponse.images])
        } catch (e) {
            console.error('Failed to parse upload response:', e)
        }
    }, [])

    const onSubmit = async (data: EventType) => {
        data.custom_image = customImage ?? undefined

        const unpricedSeats =
            locationData?.auditorium?.seats?.filter((seat) => !ticketData.some((ticket) => ticket.seat_id === seat.seat_id)) ?? []
        const saveEvent = async () => {
            const dto = {
                event: {
                    event_id: initialData?.event_id ?? generatedEventId,
                    show_id: data.show_id,
                    location_id: locationData?.location_id,
                    description: data.description,
                    custom_image: data.custom_image,
                    social_media_link: data.social_media_link,
                    local_selling: data.local_selling ?? false,
                    selling_start_date: data.selling_start_date,
                    selling_end_date: data.selling_end_date,
                    event_date: data.event_date,
                    seat_selling_mode: locationData?.default_seat_selling_mode as SeatSellingMode,
                },
                tickets: ticketData.map(({ seat_id, price, event_id }) => ({
                    seat_id,
                    price,
                    event_id,
                    status: SeatStatus.Available,
                })),
            }

            try {
                if (isEditMode && initialData?.event_id) {
                    await updateEvent(initialData.event_id, dto, partnerId)
                } else {
                    await createEvent(dto as CreateEventDto)
                }
                onClose?.()
            } catch (error) {
                console.error('Error saving event:', error)
            }
        }

        if (unpricedSeats.length > 0 && showConfirmDialog) {
            showConfirmDialog(
                () => saveEvent(),
                'Figyelmeztetés',
                'Egyes férőhelyekhez nincs ár rendelve – ezek a helyek nem lesznek értékesíthetőek. Biztosan menteni szeretné az eseményt?',
            )
        } else {
            await saveEvent()
        }
    }

    return {
        control,
        setValue,
        handleSubmit: handleSubmit(onSubmit),
        onSubmit,
        errors,
        loading,
        ticketData,
        setTicketData,
        customImageOptions,
        setCustomImageOptions,
        customImage,
        setCustomImage,
        onUpload,
        setUploadedImages,
        fields,
        append,
        remove,
    }
}
